import Dexie, { type Collection, type Table } from 'dexie';
import { isNumber } from 'lodash';

import { Axios } from '../api';
import { LOGGER_EVENT_KEYS, REGISTERS_API_URL } from '../constants';
import { logger } from '../log';
import { MAX_FETCH_PROGRESS, retrieveFullPaginatedData } from '../pagination';
import { mapItemResponseToModels } from '../transformers';
import {
    BOOLEAN_VALUE,
    STOCK_STATUS,
    type StockDetail,
    type StockDivergence,
} from '../types';
import { searchService } from './search-service';

import type { AppDatabase } from '../db';
import type { StockAdjustmentEvent } from '../firestore';
import type {
    Item,
    ItemVariant,
    ItemVariantComposition,
    ItemWithVariants,
    MeasurementUnit,
    Modifier,
    VariantDetailsMap,
    VariantWithComposition,
} from '../models';
import type { MappedItemResponse } from '../types/item';

const MAX_VARIANT_SEARCH_RESULT = 10;
export const ITEM_BATCH_SIZE = 300;

export class ItemService {
    constructor(private readonly db: AppDatabase) {
        this.db = db;
    }

    async fetchAndBulkUpsert(
        lastSyncTime?: string,
        progressCallback?: (progress: number, nextPage?: number) => void,
        batchSize = ITEM_BATCH_SIZE,
        maxItems?: number,
        startPage?: number,
    ): Promise<{ hasMoreItems: boolean; nextPage: number }> {
        const params = {
            timestamp: lastSyncTime ?? null,
            isActive: lastSyncTime ? null : true,
        };
        const hasPaginationOptions =
            maxItems !== undefined || startPage !== undefined;

        const result = await retrieveFullPaginatedData<MappedItemResponse>(
            REGISTERS_API_URL.PRODUCT.ITEMS,
            params,
            async (response, progress, nextPage) => {
                await this.bulkUpsert(response);
                progressCallback?.(progress, nextPage);
            },
            batchSize,
            hasPaginationOptions ? { maxItems, startPage } : undefined,
        );

        progressCallback?.(MAX_FETCH_PROGRESS);

        return { hasMoreItems: result.hasMore, nextPage: result.nextPage };
    }

    async bulkUpsert(response: MappedItemResponse[]) {
        const output = mapItemResponseToModels(response);

        await this.db.items.bulkPut(output.items);
        await this.db.itemVariants.bulkPut(output.itemVariants);
        await this.db.itemVariantCompositions.bulkPut(
            output.itemVariantCompositions,
        );

        searchService
            .upsertItems(output.items, output.itemVariants)
            .catch((error: unknown) => {
                logger.error({
                    eventName: LOGGER_EVENT_KEYS.SEARCH.UPSERT_ERROR,
                    message:
                        error instanceof Error ? error.message : String(error),
                });
            });
    }

    async searchAndUpsert(searchTerm: string): Promise<boolean> {
        const { data } = await Axios.get<{ content: MappedItemResponse[] }>(
            REGISTERS_API_URL.PRODUCT.SEARCH,
            { params: { searchTerm, isActive: 'true', page: '0', size: '10' } },
        );
        if (!data.content.length) return false;
        await this.bulkUpsert(data.content);
        return true;
    }

    async updateVariantStock(event: StockAdjustmentEvent) {
        const variantIds = Object.keys(event.items);

        const variants = await this.db.itemVariants
            .where('id')
            .anyOf(variantIds)
            .toArray();

        const update = variants.map((variant) => {
            const eventData = event.items[variant.id];

            return {
                key: variant.id,
                changes: { currentStock: eventData.availableQuantity },
            };
        });

        return this.db.itemVariants.bulkUpdate(update);
    }

    async fetchAndBulkUpdateStock(
        lastSyncTime: string,
        measureDivergence = false,
    ) {
        const params = {
            timestamp: lastSyncTime,
        };

        const totalDivergence: StockDivergence[] = [];

        await retrieveFullPaginatedData<MappedItemResponse>(
            REGISTERS_API_URL.PRODUCT.ITEM_STOCKS,
            params,
            async (response) => {
                const output = mapItemResponseToModels(response);

                if (measureDivergence) {
                    const divergence = await this.logStockDivergence(
                        output.itemVariants,
                    );

                    totalDivergence.push(...(divergence ?? []));
                }

                await this.bulkUpdateItemStock(output.itemVariants);
            },
        );

        return totalDivergence;
    }

    async bulkUpdateItemStock(itemVariants: ItemVariant[]) {
        const mappedVariants = itemVariants.map((variant) => ({
            key: variant.id,
            changes: {
                currentStock: variant.currentStock,
                lowStockAlert: variant.lowStockAlert,
            },
        }));

        await this.db.itemVariants.bulkUpdate(mappedVariants);
    }

    async getActiveItemsCount() {
        return this.db.items
            .where('isActive')
            .equals(BOOLEAN_VALUE.TRUE)
            .count();
    }

    async fetchPaginatedItemIds(
        searchTerm: string,
        category: string[],
        offset: number,
        pageSize: number,
    ): Promise<{ itemIds: string[]; hasMore: boolean }> {
        const lowerSearch = searchTerm.toLowerCase().replace(/ /g, '');
        const limitForCheckingMore = pageSize + 1;

        let baseItemQuery: Collection<Item, string> | Table<Item>;
        let matchingItemIdsFromVariants: string[] = [];

        if (searchTerm) {
            const matchedVariants = await this.searchVariantsByBarcodeOrSku(
                lowerSearch,
                { limit: MAX_VARIANT_SEARCH_RESULT },
            );

            matchingItemIdsFromVariants = matchedVariants.map(
                (variant) => variant.itemId,
            );
        }

        if (searchTerm) {
            baseItemQuery = this.db.items
                .where('searchTokens')
                .startsWith(lowerSearch);

            if (category.length) {
                baseItemQuery = baseItemQuery.and((item) =>
                    category.includes(item.categoryId),
                );
            }
        } else if (category.length) {
            baseItemQuery = this.db.items.where('categoryId').anyOf(category);
        } else {
            baseItemQuery = this.db.items
                .where('[isActive+nameLower]')
                .between(
                    [BOOLEAN_VALUE.TRUE, Dexie.minKey],
                    [BOOLEAN_VALUE.TRUE, Dexie.maxKey],
                );
        }

        const rawMatchedItems = await baseItemQuery
            .offset(offset)
            .limit(limitForCheckingMore)
            .toArray();

        const hasMore = rawMatchedItems.length > pageSize;
        const itemIdsToFetchDetailsFor = rawMatchedItems
            .slice(0, pageSize)
            .map((item) => item.id);

        const uniqueItemIds = Array.from(
            new Set([
                ...itemIdsToFetchDetailsFor,
                ...matchingItemIdsFromVariants,
            ]),
        );

        return {
            itemIds: uniqueItemIds,
            hasMore,
        };
    }

    async fetchItemByIds(itemIds: string[]): Promise<Item[]> {
        if (itemIds.length === 0) {
            return [];
        }

        const items = await this.db.items
            .where('[id+isActive]')
            .anyOf(itemIds.map((id) => [id, BOOLEAN_VALUE.TRUE]))
            .sortBy('nameLower');

        return items;
    }

    async fetchItemWithVariantsByIds(
        itemIds: string[],
    ): Promise<ItemWithVariants[]> {
        if (itemIds.length === 0) {
            return [];
        }

        const unorderedItems = await this.db.items
            .where('[id+isActive]')
            .anyOf(itemIds.map((id) => [id, BOOLEAN_VALUE.TRUE]))
            .toArray();

        if (unorderedItems.length === 0) {
            return [];
        }

        const itemMap = new Map(unorderedItems.map((item) => [item.id, item]));
        const items = itemIds
            .map((id) => itemMap.get(id))
            .filter((item): item is Item => !!item);

        const modifierGroupIds = [
            ...new Set(
                items.flatMap((item) =>
                    (item.modifierGroups ?? [])
                        .filter((modifier) => !modifier.deleted)
                        .map((modifier) => modifier.groupId),
                ),
            ),
        ];

        const [variants, measurementUnits, modifiers] = await Promise.all([
            this.db.itemVariants
                .where('[itemId+deleted]')
                .anyOf(items.map((item) => [item.id, BOOLEAN_VALUE.FALSE]))
                .toArray(),
            this.db.measurementUnits
                .where('[id+deleted]')
                .anyOf(
                    items.map((item) => [
                        item.measurementUnitId ?? '',
                        BOOLEAN_VALUE.FALSE,
                    ]),
                )
                .toArray(),
            this.db.modifiers
                .where('[id+deleted+active]')
                .anyOf(
                    modifierGroupIds.map((id) => [
                        id,
                        BOOLEAN_VALUE.FALSE,
                        BOOLEAN_VALUE.TRUE,
                    ]),
                )
                .toArray(),
        ]);

        if (variants.length === 0) {
            return [];
        }

        const measurementUnitMap = new Map(
            measurementUnits.map((u) => [u.id, u]),
        );

        const modifierMap = new Map(
            modifiers.map((modifier) => [modifier.id, modifier]),
        );

        const { variantsByItemId } =
            await this.applyVariantStockOverride(variants);

        return this.buildItemsWithVariants(
            items,
            variantsByItemId,
            measurementUnitMap,
            modifierMap,
        );
    }

    async getCompositionVariantMap(variantIds: string[]) {
        const compositions = await this.db.itemVariantCompositions
            .where('[parentVariantId+deleted]')
            .anyOf(
                variantIds.map((variantId) => [variantId, BOOLEAN_VALUE.FALSE]),
            )
            .toArray();

        const componentVariantIds = compositions.map((c) => c.variantId);
        const componentVariants = await this.db.itemVariants
            .where('id')
            .anyOf(componentVariantIds)
            .toArray();

        const componentVariantMap = new Map(
            componentVariants.map((v) => [v.id, v]),
        );

        const compositionsByParent = new Map<
            string,
            ItemVariantComposition[]
        >();

        for (const composition of compositions) {
            const existing =
                compositionsByParent.get(composition.parentVariantId) ?? [];
            existing.push(composition);
            compositionsByParent.set(composition.parentVariantId, existing);
        }

        return { componentVariantMap, compositionsByParent };
    }

    async getById(id: string) {
        return this.db.items
            .where('[id+isActive]')
            .equals([id, BOOLEAN_VALUE.TRUE])
            .first();
    }

    async getVariantBySKUOrBarcode(skuOrBarcode: string) {
        const variants = await this.searchVariantsByBarcodeOrSku(skuOrBarcode, {
            limit: 1,
        });
        const variantId = variants[0]?.id;

        if (!variantId) {
            return undefined;
        }

        const detailsMap = await this.getVariantDetailsMap([variantId]);
        return Reflect.get(detailsMap, variantId) ?? undefined;
    }

    async getVariantDetailsMap(variantIds: string[]) {
        const variants = await this.db.itemVariants
            .where('id')
            .anyOf(variantIds)
            .toArray();

        const { variantsWithOverrides } =
            await this.applyVariantStockOverride(variants);

        const items = await this.db.items
            .where('id')
            .anyOf(variantsWithOverrides.map((variant) => variant.itemId))
            .toArray();

        const additionalUnitIds = items.flatMap(
            (item) => item.additionalUnits?.map((u) => u.unitId) ?? [],
        );

        const allUnitIds = [
            ...items.map((item) => item.measurementUnitId ?? ''),
            ...additionalUnitIds,
        ];

        const measurementUnits = await this.db.measurementUnits
            .where('id')
            .anyOf(allUnitIds)
            .toArray();

        const measurementUnitMap = new Map(
            measurementUnits.map((u) => [u.id, u]),
        );

        const variantMap = new Map(
            variantsWithOverrides.map((variant) => [variant.id, variant]),
        );
        const itemMap = new Map(items.map((item) => [item.id, item]));

        return variantIds.reduce(
            (acc, variantId) => {
                const variant = variantMap.get(variantId);
                const item = itemMap.get(variant?.itemId ?? '');

                const measurementUnit = item?.measurementUnitId
                    ? measurementUnitMap.get(item.measurementUnitId)
                    : undefined;

                const additionalUnits = (item?.additionalUnits ?? []).map(
                    (u) => ({
                        ...u,
                        measurementUnit: measurementUnitMap.get(u.unitId),
                    }),
                );

                Reflect.set(acc, variantId, {
                    item,
                    variant,
                    measurementUnit,
                    additionalUnits,
                });

                return acc;
            },
            {} as Record<string, VariantDetailsMap>,
        );
    }

    async getVariantsAndCompositions(
        variantIds: string[],
        modifierIds: string[] = [],
    ) {
        const compositionsInVariants = await this.db.itemVariantCompositions
            .where('[parentVariantId+deleted]')
            .anyOf(
                variantIds.map((variantId) => [variantId, BOOLEAN_VALUE.FALSE]),
            )
            .toArray();

        const modifiers = await this.db.modifiers
            .where('id')
            .anyOf(modifierIds)
            .toArray();

        const variantIdsInModifiers = modifiers
            .flatMap((modifier) => modifier.options)
            .map((option) => option.linkedVariantId)
            .filter((option) => !!option);

        const lookupVariantids = [
            ...variantIds,
            ...compositionsInVariants.map(
                (composition) => composition.variantId,
            ),
            ...variantIdsInModifiers,
        ];

        const uniqueVariants = await this.db.itemVariants
            .where('id')
            .anyOf(lookupVariantids)
            .distinct()
            .toArray();

        const items = await this.db.items
            .where('id')
            .anyOf(uniqueVariants.map((variant) => variant.itemId))
            .toArray();

        const modifierMap = new Map(
            modifiers.map((modifier) => [modifier.id, modifier]),
        );

        const variantMap = new Map(
            uniqueVariants.map((variant) => [variant.id, variant]),
        );

        return {
            variants: variantMap,
            compositions: compositionsInVariants,
            items,
            modifiers: modifierMap,
        };
    }

    async applyOptimisticStockDeduction(
        variants: { id: string; currentStock: number }[],
    ) {
        await this.db.itemVariants.bulkUpdate(
            variants.map((variant) => ({
                key: variant.id,
                changes: { currentStock: variant.currentStock },
            })),
        );
    }

    async fetchItemCategoryIds(itemIds: string[]): Promise<string[]> {
        if (itemIds.length === 0) {
            return [];
        }

        const items = await this.db.items
            .where('[id+isActive]')
            .anyOf(itemIds.map((id) => [id, BOOLEAN_VALUE.TRUE]))
            .sortBy('nameLower');

        if (items.length === 0) {
            return [];
        }

        return items.map((item) => item.categoryId).filter(Boolean);
    }

    private async logStockDivergence(sourceVariants: ItemVariant[]) {
        try {
            const variantIds = sourceVariants.map((v) => v.id);
            const existingVariants =
                await this.db.itemVariants.bulkGet(variantIds);

            const changedVariants: Array<StockDivergence> = [];

            for (const [index, sourceVariant] of sourceVariants.entries()) {
                const existing = Reflect.get(existingVariants, index);
                if (!existing) continue;

                if (
                    existing.currentStock !== sourceVariant.currentStock ||
                    existing.lowStockAlert !== sourceVariant.lowStockAlert
                ) {
                    changedVariants.push({
                        variant_id: sourceVariant.id,

                        local_stock: existing.currentStock,
                        local_low_stock_alert: existing.lowStockAlert,

                        source_stock: sourceVariant.currentStock,
                        source_low_stock_alert: sourceVariant.lowStockAlert,
                    });
                }
            }

            return changedVariants;
        } catch {
            logger.error('Error calculating stock divergence');
        }
    }

    private resolveLowestCompositionStock(
        compositions: ItemVariantComposition[],
        componentVariantMap: Map<string, ItemVariant>,
    ): StockDetail | null {
        let lowestStock: StockDetail | null = null;

        for (const composition of compositions) {
            const child = componentVariantMap.get(composition.variantId);

            if (!isNumber(child?.currentStock)) continue;

            const availableStock = Math.floor(
                child.currentStock / composition.quantity,
            );

            if (!lowestStock || availableStock < lowestStock.currentStock) {
                lowestStock = {
                    currentStock: availableStock,
                    lowStockAlert: child.lowStockAlert ?? 0,
                    status: STOCK_STATUS.IN_STOCK,
                };
            }
        }

        return lowestStock;
    }

    private async applyVariantStockOverride(variants: ItemVariant[]) {
        const { componentVariantMap, compositionsByParent } =
            await this.getCompositionVariantMap(variants.map((v) => v.id));

        const variantsByItemId = new Map<string, ItemVariant[]>();
        const variantsWithOverrides: ItemVariant[] = [];

        for (const variant of variants) {
            const compositionsInVariant = compositionsByParent.get(variant.id);

            let processedVariant = variant;

            if (compositionsInVariant?.length) {
                const lowestStock = this.resolveLowestCompositionStock(
                    compositionsInVariant,
                    componentVariantMap,
                );

                if (lowestStock) {
                    processedVariant = {
                        ...variant,
                        currentStock: lowestStock.currentStock,
                        lowStockAlert: lowestStock.lowStockAlert,
                    };
                }
            }

            variantsWithOverrides.push(processedVariant);

            const itemVariants = variantsByItemId.get(variant.itemId) ?? [];
            itemVariants.push(processedVariant);
            variantsByItemId.set(variant.itemId, itemVariants);
        }

        return { variantsWithOverrides, variantsByItemId };
    }

    private buildItemsWithVariants(
        items: Item[],
        variantsByItemId: Map<string, ItemVariant[]>,
        measurementUnitMap: Map<string, MeasurementUnit>,
        modifierMap: Map<string, Modifier>,
    ): ItemWithVariants[] {
        const result: ItemWithVariants[] = [];

        for (const item of items) {
            const variants = variantsByItemId.get(item.id);
            if (!variants?.length) continue;

            const modifiers = (item.modifierGroups ?? []).flatMap((group) => {
                if (group.deleted) return [];

                const modifier = modifierMap.get(group.groupId);

                if (!modifier) return [];

                return [{ ...modifier, displayOrder: group.displayOrder }];
            });

            result.push({
                ...item,
                variants: variants as VariantWithComposition[],
                measurementUnit: item.measurementUnitId
                    ? measurementUnitMap.get(item.measurementUnitId)
                    : undefined,
                modifiers,
            });
        }

        return result;
    }

    private async searchVariantsByBarcodeOrSku(
        search: string,
        options: { limit: number },
    ): Promise<ItemVariant[]> {
        const lowerSearch = search.toLowerCase();

        const matches = await this.db.itemVariants
            .where('sku')
            .startsWithIgnoreCase(lowerSearch)
            .or('barcode')
            .startsWithIgnoreCase(lowerSearch)
            .or('barcodes')
            .startsWithIgnoreCase(lowerSearch)
            .distinct()
            .filter((v) => v.deleted === BOOLEAN_VALUE.FALSE)
            .limit(options.limit)
            .toArray();

        return matches;
    }
}
